/**
 * ForecastDial — Monte Carlo Yield Forecast Visualization
 * A semicircular gauge dial with animated needle showing ProfitSpot Score
 * Elite-tier only feature with blurred preview for other tiers
 */
import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Shield, Clock, Zap, Lock, Crown, Fuel } from 'lucide-react';
import { FeatureInfo } from './FeatureInfo';

// ─── Color Constants (ProfitSpot Dark Theme) ─────────────────────
const COLORS = {
  bg: '#0a0e17',
  card: '#141926',
  border: '#1e2a3a',
  accent: '#8b5cf6',
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  cyan: '#06b6d4',
  textPrimary: '#ffffff',
  textSecondary: '#9ca3af',
  textMuted: '#6b7280',
};

const ZONE_COLORS = {
  danger: COLORS.red,
  caution: COLORS.yellow,
  favorable: COLORS.green,
  optimal: COLORS.cyan,
};

const ZONE_LABELS = {
  danger: 'DANGER',
  caution: 'CAUTION',
  favorable: 'FAVORABLE',
  optimal: 'OPTIMAL',
};

// ─── Loading Skeleton ────────────────────────────────────────────
const DialSkeleton = () => (
  <div className="animate-pulse" style={{ padding: '24px' }}>
    <div style={{ 
      width: '200px', 
      height: '100px', 
      background: `linear-gradient(90deg, ${COLORS.card} 0%, ${COLORS.border} 50%, ${COLORS.card} 100%)`,
      borderRadius: '100px 100px 0 0',
      margin: '0 auto 16px',
    }} />
    <div style={{ 
      width: '60px', 
      height: '32px', 
      background: COLORS.border, 
      borderRadius: '8px',
      margin: '0 auto 8px',
    }} />
    <div style={{ 
      width: '80px', 
      height: '16px', 
      background: COLORS.border, 
      borderRadius: '4px',
      margin: '0 auto',
    }} />
  </div>
);

// ─── Elite Upgrade CTA ───────────────────────────────────────────
const EliteUpgradeCTA = ({ onUpgrade }) => (
  <div style={{
    position: 'absolute',
    inset: 0,
    background: 'rgba(10, 14, 23, 0.85)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    zIndex: 10,
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
    }}>
      <Crown style={{ width: '24px', height: '24px', color: '#ffa502' }} />
      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffa502', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Elite Feature
      </span>
    </div>
    <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: COLORS.textPrimary, marginBottom: '8px', textAlign: 'center' }}>
      Forecast Dial
    </h4>
    <p style={{ fontSize: '12px', color: COLORS.textSecondary, marginBottom: '16px', textAlign: 'center', maxWidth: '200px' }}>
      Monte Carlo simulation with 1,000 scenarios to predict yield outcomes
    </p>
    <button
      onClick={onUpgrade}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        background: COLORS.green,
        color: '#000',
        fontWeight: 'bold',
        fontSize: '14px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseEnter={(e) => e.target.style.background = '#0d9668'}
      onMouseLeave={(e) => e.target.style.background = COLORS.green}
    >
      <Lock style={{ width: '16px', height: '16px' }} />
      Upgrade to Elite
    </button>
  </div>
);

// ─── SVG Dial Component ──────────────────────────────────────────
const SemiCircularDial = ({ score, zone, animated = true, size: propSize = 200 }) => {
  const [displayScore, setDisplayScore] = useState(0);
  
  useEffect(() => {
    if (animated) {
      const timer = setTimeout(() => setDisplayScore(score), 100);
      return () => clearTimeout(timer);
    } else {
      setDisplayScore(score);
    }
  }, [score, animated]);
  
  const size = propSize;
  const strokeWidth = size * 0.1; // Scale stroke with size
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  
  // Calculate needle angle (0-180 degrees, left to right)
  const needleAngle = (displayScore / 100) * 180;
  
  // Arc path for the dial background segments
  const createArc = (startAngle, endAngle, color) => {
    const start = polarToCartesian(center, center, radius, startAngle);
    const end = polarToCartesian(center, center, radius, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    return {
      path: `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
      color,
    };
  };
  
  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 180) * Math.PI / 180;
    return {
      x: cx + r * Math.cos(rad),
      y: cy + r * Math.sin(rad),
    };
  };
  
  // Dial segments: danger (0-30), caution (31-55), favorable (56-80), optimal (81-100)
  const segments = [
    createArc(0, 54, COLORS.red),      // 0-30 → danger
    createArc(54, 99, COLORS.yellow),  // 31-55 → caution
    createArc(99, 144, COLORS.green),  // 56-80 → favorable
    createArc(144, 180, COLORS.cyan),  // 81-100 → optimal
  ];
  
  // Needle position
  const needleEnd = polarToCartesian(center, center, radius - 15, needleAngle);
  
  return (
    <div style={{ position: 'relative', width: `${size}px`, height: `${size / 2 + 20}px`, margin: '0 auto' }}>
      <svg width={size} height={size / 2 + 20} viewBox={`0 0 ${size} ${size / 2 + 20}`}>
        {/* Background arc segments */}
        {segments.map((seg, i) => (
          <path
            key={i}
            d={seg.path}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            opacity={0.3}
          />
        ))}
        
        {/* Active segment highlight */}
        <path
          d={createArc(0, needleAngle, ZONE_COLORS[zone] || COLORS.green).path}
          fill="none"
          stroke={ZONE_COLORS[zone] || COLORS.green}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          style={{
            transition: animated ? 'stroke-dashoffset 1.2s ease-out' : 'none',
            filter: zone === 'optimal' ? `drop-shadow(0 0 8px ${COLORS.cyan})` : 'none',
          }}
        />
        
        {/* Needle */}
        <g style={{ 
          transform: `rotate(${needleAngle - 90}deg)`, 
          transformOrigin: `${center}px ${center}px`,
          transition: animated ? 'transform 1.2s ease-out' : 'none',
        }}>
          <line
            x1={center}
            y1={center}
            x2={center}
            y2={center - radius + 25}
            stroke={COLORS.textPrimary}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={center} cy={center} r="8" fill={COLORS.card} stroke={COLORS.textPrimary} strokeWidth="2" />
        </g>
        
        {/* Tick marks */}
        {[0, 30, 55, 80, 100].map((tick) => {
          const angle = (tick / 100) * 180;
          const outer = polarToCartesian(center, center, radius + 5, angle);
          const inner = polarToCartesian(center, center, radius - strokeWidth / 2 - 8, angle);
          return (
            <line
              key={tick}
              x1={outer.x}
              y1={outer.y}
              x2={inner.x}
              y2={inner.y}
              stroke={COLORS.textMuted}
              strokeWidth="1"
            />
          );
        })}
      </svg>
    </div>
  );
};

// ─── Stats Card ──────────────────────────────────────────────────
const StatCard = ({ label, value, subValue, icon: Icon, color, isPercentage = false }) => (
  <div style={{
    background: COLORS.card,
    borderRadius: '8px',
    padding: '12px',
    border: `1px solid ${COLORS.border}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
      {Icon && <Icon style={{ width: '14px', height: '14px', color: COLORS.textMuted }} />}
      <span style={{ fontSize: '10px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: '18px', fontWeight: 'bold', color: color || COLORS.textPrimary }}>
      {isPercentage ? `${value?.toFixed(1)}%` : value}
    </div>
    {subValue && (
      <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginTop: '2px' }}>
        {subValue}
      </div>
    )}
  </div>
);

// ─── Main ForecastDial Component ─────────────────────────────────
const ForecastDial = ({ 
  poolId, 
  symbol, 
  currentApy, 
  tvl, 
  ilRisk, 
  chain, 
  protocol,
  userTier = 'free',
  isEliteOverride = null,
  onUpgrade,
  apiUrl,
  token,
  syncedDays = null,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [investmentAmount, setInvestmentAmount] = useState(1000);
  
  // Map synced days to valid forecast intervals, or use internal state
  const validDays = [30, 90, 180, 365];
  const mapToValidDays = (d) => {
    if (!d) return 30;
    if (d <= 45) return 30;
    if (d <= 120) return 90;
    if (d <= 270) return 180;
    return 365;
  };
  
  const [internalDays, setInternalDays] = useState(30);
  const days = syncedDays ? mapToValidDays(syncedDays) : internalDays;
  const setDays = syncedDays ? () => {} : setInternalDays; // Disable toggle if synced
  
  const isElite = isEliteOverride !== null ? isEliteOverride : (userTier === 'elite' || userTier === 'unlimited');
  const isGated = !isElite;
  
  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Fetch forecast data
  useEffect(() => {
    const fetchForecast = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${apiUrl}/api/forecast/simulate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            pool_id: poolId,
            symbol,
            current_apy: currentApy,
            tvl,
            il_risk: ilRisk || 'medium',
            chain: chain || 'ethereum',
            protocol: protocol || 'unknown',
            days,
            investment_amount: investmentAmount,
          }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch forecast');
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Forecast error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (poolId && currentApy > 0) {
      fetchForecast();
    }
  }, [poolId, symbol, currentApy, tvl, ilRisk, chain, protocol, days, apiUrl, token, investmentAmount]);

  // Extract gas analysis from response
  const gasAnalysis = data?.gas_analysis || null;
  
  // Time horizon options
  const timeOptions = [
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
    { value: 180, label: '180d' },
    { value: 365, label: '1Y' },
  ];
  
  // Risk level display
  const getRiskDisplay = (ilRisk) => {
    const map = {
      low: { label: 'Low', color: COLORS.green, icon: Shield },
      medium: { label: 'Medium', color: COLORS.yellow, icon: AlertTriangle },
      high: { label: 'High', color: COLORS.red, icon: AlertTriangle },
    };
    return map[ilRisk?.toLowerCase()] || map.medium;
  };
  
  const riskDisplay = getRiskDisplay(ilRisk);
  
  // Determine colors for ROI values
  const getROIColor = (value) => {
    if (value > 0) return COLORS.green;
    if (value < 0) return COLORS.red;
    return COLORS.textSecondary;
  };
  
  if (loading) {
    return (
      <div style={{
        background: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        <DialSkeleton />
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{
        background: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        padding: '24px',
        textAlign: 'center',
      }}>
        <AlertTriangle style={{ width: '32px', height: '32px', color: COLORS.red, margin: '0 auto 12px' }} />
        <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Failed to load forecast</p>
      </div>
    );
  }
  
  // For gated users, show blurred preview
  const displayData = isGated ? {
    profitspot_score: 65,
    dial_zone: 'favorable',
    percentiles: { p10: 2.5, p25: 5.0, p50: 8.5, p75: 12.0, p90: 18.0 },
    probability_profitable: 0.82,
    mean_roi: 9.2,
    median_roi: 8.5,
    std_dev: 6.3,
    risk_factors: ['Sample risk factor'],
  } : data;
  
  return (
    <div style={{
      background: COLORS.card,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Header */}
      <div style={{
        padding: isMobile ? '12px' : '16px',
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '12px' : '0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Zap style={{ width: '18px', height: '18px', color: COLORS.accent }} />
          <span style={{ fontSize: '14px', fontWeight: 'bold', color: COLORS.textPrimary }}>Forecast Dial</span>
          {!isElite && (
            <span style={{
              fontSize: '10px',
              padding: '2px 8px',
              background: 'rgba(255, 165, 2, 0.2)',
              color: '#ffa502',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}>
              ELITE
            </span>
          )}
        </div>
        
        {/* Time Horizon Selector */}
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {syncedDays ? (
            <span style={{
              padding: '4px 10px',
              fontSize: '11px',
              color: COLORS.textSecondary,
              background: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '6px',
            }}>
              Synced: {days}d
            </span>
          ) : (
            timeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => !isGated && setDays(opt.value)}
                disabled={isGated}
                style={{
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: days === opt.value ? 'bold' : 'normal',
                  background: days === opt.value ? COLORS.accent : 'transparent',
                  color: days === opt.value ? COLORS.textPrimary : COLORS.textSecondary,
                  border: `1px solid ${days === opt.value ? COLORS.accent : COLORS.border}`,
                  borderRadius: '6px',
                  cursor: isGated ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isGated ? 0.5 : 1,
                }}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>

        {/* Investment Amount Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
          <span style={{ fontSize: '11px', color: COLORS.textMuted }}>Investment $</span>
          <input
            type="number"
            value={investmentAmount}
            onChange={(e) => setInvestmentAmount(Math.max(1, Number(e.target.value)))}
            disabled={isGated}
            style={{
              width: '80px', padding: '3px 8px', fontSize: '12px',
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              borderRadius: '6px', color: COLORS.textPrimary,
              opacity: isGated ? 0.5 : 1,
            }}
            data-testid="investment-amount-input"
          />
        </div>
      </div>
      
      {/* Content (blurred if gated) */}
      <div style={{
        padding: '20px',
        filter: isGated ? 'blur(6px)' : 'none',
        pointerEvents: isGated ? 'none' : 'auto',
      }}>
        {/* Dial */}
        <SemiCircularDial 
          score={displayData?.profitspot_score || 50} 
          zone={displayData?.dial_zone || 'caution'}
          animated={!isGated}
          size={isMobile ? 160 : 200}
        />
        
        {/* Score Display */}
        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <div style={{ 
            fontSize: isMobile ? '36px' : '48px', 
            fontWeight: 'bold', 
            color: COLORS.textPrimary,
            lineHeight: 1,
          }}>
            {displayData?.profitspot_score || '—'}
          </div>
          <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px' }}>
            ProfitSpot Score
          </div>
          <div style={{
            display: 'inline-block',
            marginTop: '8px',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: ZONE_COLORS[displayData?.dial_zone] || COLORS.yellow,
            background: `${ZONE_COLORS[displayData?.dial_zone] || COLORS.yellow}20`,
            border: `1px solid ${ZONE_COLORS[displayData?.dial_zone] || COLORS.yellow}40`,
          }}>
            {ZONE_LABELS[displayData?.dial_zone] || 'CAUTION'}
          </div>
        </div>
        
        {/* Stats Grid (2x2 on desktop, 1 column on mobile) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: '12px',
          marginTop: '20px',
        }}>
          <StatCard
            label="Median ROI"
            value={displayData?.percentiles?.p50}
            subValue={`${days}d projection`}
            icon={TrendingUp}
            color={getROIColor(displayData?.percentiles?.p50)}
            isPercentage
          />
          <StatCard
            label="Prob. Profitable"
            value={(displayData?.probability_profitable * 100)}
            subValue="of scenarios"
            icon={Zap}
            color={displayData?.probability_profitable >= 0.7 ? COLORS.green : displayData?.probability_profitable >= 0.5 ? COLORS.yellow : COLORS.red}
            isPercentage
          />
          <StatCard
            label="Risk Level"
            value={riskDisplay.label}
            icon={riskDisplay.icon}
            color={riskDisplay.color}
          />
          <StatCard
            label="Best / Worst"
            value={`${displayData?.percentiles?.p90?.toFixed(1)}% / ${displayData?.percentiles?.p10?.toFixed(1)}%`}
            subValue="p90 / p10"
            icon={TrendingDown}
            color={COLORS.textSecondary}
          />
        </div>
        
        {/* Risk Factors */}
        {displayData?.risk_factors?.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontSize: '10px', color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: '8px' }}>
              Risk Factors
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {displayData.risk_factors.slice(0, 3).map((factor, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '11px',
                  color: COLORS.yellow,
                }}>
                  <AlertTriangle style={{ width: '12px', height: '12px', flexShrink: 0 }} />
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time-to-Profit (Feature 1 — Gas-Adjusted TTP) */}
        {gasAnalysis && (
          <div style={{ marginTop: '16px', padding: '12px', background: COLORS.bg, borderRadius: '8px', border: `1px solid ${COLORS.border}` }} data-testid="ttp-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <Fuel style={{ width: '14px', height: '14px', color: COLORS.cyan }} />
              <span style={{ fontSize: '12px', fontWeight: 700, color: COLORS.textPrimary }}>Time to Profit</span>
              <FeatureInfo featureKey="gas_ttp" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: COLORS.textMuted }}>
                Gas (entry + exit on {gasAnalysis.chain}):
              </span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textSecondary }}>
                ${gasAnalysis.estimated_gas_usd}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: COLORS.textMuted }}>Break-even after gas:</span>
              <span style={{
                fontSize: '14px', fontWeight: 700,
                color: gasAnalysis.days_to_recover_gas <= 7 ? COLORS.green :
                       gasAnalysis.days_to_recover_gas <= 30 ? COLORS.yellow : COLORS.red,
              }}>
                {gasAnalysis.days_to_recover_gas > 999 ? 'Never' : `${gasAnalysis.days_to_recover_gas} days`}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: COLORS.textMuted }}>Gas as % of investment:</span>
              <span style={{ fontSize: '11px', color: COLORS.textSecondary }}>{gasAnalysis.gas_as_pct_of_investment}%</span>
            </div>
            {gasAnalysis.l2_comparison && gasAnalysis.chain?.toLowerCase() !== 'base' && (
              <div style={{
                marginTop: '8px', padding: '8px 10px', background: `${COLORS.cyan}10`,
                borderRadius: '6px', border: `1px solid ${COLORS.cyan}30`,
              }}>
                <span style={{ fontSize: '11px', color: COLORS.cyan }}>
                  On {gasAnalysis.l2_comparison.chain}: ${gasAnalysis.l2_comparison.gas_usd} gas, break-even in {gasAnalysis.l2_comparison.days_to_recover} days
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Simulation Info */}
        <div style={{
          marginTop: '16px',
          padding: '8px 12px',
          background: COLORS.bg,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '10px', color: COLORS.textMuted }}>
            Monte Carlo · 1,000 simulations
          </span>
          <span style={{ fontSize: '10px', color: COLORS.textMuted }}>
            σ = {displayData?.std_dev?.toFixed(1)}%
          </span>
        </div>
      </div>
      
      {/* Upgrade CTA overlay for non-elite users */}
      {isGated && <EliteUpgradeCTA onUpgrade={onUpgrade} />}
    </div>
  );
};

export default ForecastDial;
